import { useState } from "react";
import { Menu as MenuIcon, Phone, X, ChevronRight, MapPin, Wrench, Home, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const NAV_LINKS = [
  { to: "/", label: "Home", icon: Home },
  { to: "/services", label: "All Services", icon: Wrench },
  { to: "/locations", label: "All Locations", icon: MapPin },
  { href: "#footer", label: "Contact", icon: Mail },
];

export default function Menu({ id, bgColor }) {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen((prev) => !prev);
  const closeMenu = () => setIsOpen(false);

  return (
    <div id={id}>
      {/* Burger Button */}
      <button
        onClick={toggleMenu}
        className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
        aria-label="Toggle menu"
      >
        <MenuIcon size={24} className={bgColor} />
      </button>

      {/* Overlay */}
      <div
        onClick={closeMenu}
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-all duration-300 z-40 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      />

      {/* Side Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white z-50 flex flex-col
          transition-all duration-300 ease-out
          ${isOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shadow-md shadow-blue-600/30">
              <Phone className="w-4 h-4 text-white" />
            </span>
            <span className="text-lg font-black text-gray-900 tracking-tight">Chicago Plumbing</span>
          </div>
          <button
            onClick={closeMenu}
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
            aria-label="Close menu"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Nav Links */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {NAV_LINKS.map(({ to, href, label, icon: Icon }) => {
            const inner = (
              <span className="flex items-center gap-3 px-4 py-3.5 rounded-xl hover:bg-blue-50 transition-colors group">
                <span className="w-9 h-9 bg-gray-100 group-hover:bg-blue-100 rounded-lg flex items-center justify-center transition-colors">
                  <Icon className="w-4 h-4 text-gray-500 group-hover:text-blue-600 transition-colors" />
                </span>
                <span className="flex-1 font-semibold text-gray-800 group-hover:text-blue-700 transition-colors text-[15px]">
                  {label}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400 transition-colors" />
              </span>
            );

            return to ? (
              <Link key={label} to={to} onClick={closeMenu}>
                {inner}
              </Link>
            ) : (
              <a key={label} href={href} onClick={closeMenu}>
                {inner}
              </a>
            );
          })}

          {/* Divider */}
          <div className="pt-4 pb-2 px-4">
            <p className="text-xs font-bold tracking-widest uppercase text-gray-400">Get in touch</p>
          </div>

          {/* Quick info rows */}
          <div className="px-4 space-y-3">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              </span>
              <div>
                <p className="text-xs text-gray-400 font-medium">Availability</p>
                <p className="text-sm font-bold text-gray-800">24/7 Emergency Service</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-blue-500" />
              </span>
              <div>
                <p className="text-xs text-gray-400 font-medium">Service Area</p>
                <p className="text-sm font-bold text-gray-800">Greater Chicago, IL</p>
              </div>
            </div>
          </div>
        </nav>

        {/* CTA Footer */}
        <div className="px-5 pb-8 pt-4 border-t border-gray-100 space-y-3">
          <a
            href="tel:3125550147"
            className="w-full bg-blue-600 hover:bg-blue-700 transition-colors text-white py-3.5 rounded-xl
              font-bold flex items-center justify-center gap-2 shadow-md shadow-blue-600/25 text-sm"
          >
            <Phone className="w-4 h-4" />
            (312) 555-0147
          </a>
          <a
            href="#footer"
            onClick={closeMenu}
            className="w-full border border-gray-200 hover:bg-gray-50 transition-colors text-gray-700 py-3 rounded-xl
              font-semibold flex items-center justify-center gap-2 text-sm"
          >
            <Mail className="w-4 h-4" />
            Send a Message
          </a>
        </div>
      </div>
    </div>
  );
}